/*
 * Pure Encapsulations Perks loyalty opt-in.
 *
 * Shopify renders the "Sign in or create account" modal inside its own
 * <shopify-account> web component. The marketing consent checkbox lives two
 * open shadow roots deep: shopify-account -> shopify-login-form. We clone that
 * existing consent row (so Shopify's own styling carries over) and drop a
 * loyalty opt-in checkbox directly beneath it.
 *
 * The opt-in state is stored so a later step can forward it to Yotpo with
 * opted_in=true + has_account=true once the account is created. A
 * `pe:loyalty-consent-change` event is also dispatched on document.
 */
(function () {
  'use strict';

  var LOYALTY_ID = 'pe-loyalty-consent';
  var STORAGE_KEY = 'pe_loyalty_consent';
  var LOYALTY_LABEL_HTML =
    'Enroll me in the <a href="https://www.pureforyou.com/pages/pure-encapsulations-perks">Pure Encapsulations\u00ae Perks</a> rewards program. ' +
    'I certify that I have read and agree to the Pure Encapsulations\u00ae Perks ' +
    '<a href="https://pureforyou.com/pages/pure-encapsulations%C2%AE-loyalty-rewards-program-terms-and-conditions">Loyalty Rewards Program Terms and Conditions</a> ' +
    'as well as the Pure Encapsulations <a href="https://www.pureencapsulationspro.com/privacy-policy">Privacy Policy</a> ' +
    '(which includes our <a href="https://pureforyou.com/pages/u-s-notice-of-financial-incentive">Notice of Financial Incentive</a>) ' +
    'and the <a href="https://pureforyou.com/pages/terms">Purchase Terms and Conditions</a>.';
  var CONSENT_TEXT_RE = /consent to receiving electronic/i;

  var observed = new WeakSet();

  function getStoredConsent() {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  function setStoredConsent(value) {
    try {
      localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
    } catch (e) {
      /* storage may be unavailable (private mode) */
    }
  }

  // Walk up from the checkbox to the smallest container that also holds the
  // consent copy, so the clone keeps the full row (input + label text).
  function findConsentRow(shadow) {
    var checkboxes = shadow.querySelectorAll('input[type="checkbox"]');
    for (var i = 0; i < checkboxes.length; i++) {
      var cb = checkboxes[i];
      if (cb.id === LOYALTY_ID) continue;
      var node = cb;
      for (var depth = 0; depth < 6 && node; depth++) {
        if (node.textContent && CONSENT_TEXT_RE.test(node.textContent)) {
          return node;
        }
        node = node.parentElement;
      }
    }
    // Fallback: first non-loyalty checkbox's nearest label/parent.
    for (var j = 0; j < checkboxes.length; j++) {
      if (checkboxes[j].id === LOYALTY_ID) continue;
      return checkboxes[j].closest('label') || checkboxes[j].parentElement;
    }
    return null;
  }

  // Open the consent links in a new tab so clicking them doesn't abandon the
  // half-finished signup inside the modal.
  function hardenLinks(container) {
    container.querySelectorAll('a').forEach(function (a) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
      if (!a.style.textDecoration) a.style.textDecoration = 'underline';
    });
  }

  // Drop our loyalty copy (with links) into the cloned row's text container.
  // The text container is the largest element that holds the copy but NOT the
  // checkbox itself, which isolates the label text from the row wrapper.
  function replaceRowText(root, html) {
    var candidates = [];
    root.querySelectorAll('*').forEach(function (el) {
      if (el.querySelector('input')) return;
      if (el.textContent && el.textContent.trim().length > 15) candidates.push(el);
    });

    if (candidates.length) {
      candidates.sort(function (a, b) {
        return b.textContent.length - a.textContent.length;
      });
      candidates[0].innerHTML = html;
      hardenLinks(candidates[0]);
      return;
    }

    // Fallback: copy lived in a bare text node next to the input. Strip those
    // text nodes and append a span with our markup.
    root.childNodes.forEach(function (node) {
      if (node.nodeType === 3) node.textContent = '';
    });
    var span = document.createElement('span');
    span.innerHTML = html;
    hardenLinks(span);
    root.appendChild(span);
  }

  function buildLoyaltyRow(consentRow) {
    var clone = consentRow.cloneNode(true);
    clone.setAttribute('data-pe-loyalty', '');
    clone.style.marginTop = '12px';

    // Clear copied ids to avoid duplicates inside the shadow root.
    clone.querySelectorAll('[id]').forEach(function (el) {
      el.removeAttribute('id');
    });

    var checkbox = clone.querySelector('input[type="checkbox"]');
    if (!checkbox) return null;

    checkbox.id = LOYALTY_ID;
    checkbox.name = 'pe_loyalty_consent';
    checkbox.value = 'Yes';
    checkbox.required = false;
    checkbox.removeAttribute('required');
    checkbox.removeAttribute('aria-invalid');
    checkbox.removeAttribute('checked');
    checkbox.checked = getStoredConsent();

    clone.querySelectorAll('label[for]').forEach(function (label) {
      label.setAttribute('for', LOYALTY_ID);
    });

    replaceRowText(clone, LOYALTY_LABEL_HTML);

    checkbox.addEventListener('change', function () {
      setStoredConsent(checkbox.checked);
      document.dispatchEvent(
        new CustomEvent('pe:loyalty-consent-change', {
          detail: { optedIn: checkbox.checked }
        })
      );
    });

    return clone;
  }

  function inject(shadow) {
    if (!shadow) return false;
    if (shadow.querySelector('#' + LOYALTY_ID)) return true;

    var consentRow = findConsentRow(shadow);
    if (!consentRow) return false;

    var loyaltyRow = buildLoyaltyRow(consentRow);
    if (!loyaltyRow) return false;

    consentRow.insertAdjacentElement('afterend', loyaltyRow);
    setStoredConsent(loyaltyRow.querySelector('#' + LOYALTY_ID).checked);
    return true;
  }

  function observe(shadow) {
    if (!shadow || observed.has(shadow)) return;
    observed.add(shadow);
    new MutationObserver(tick).observe(shadow, { childList: true, subtree: true });
  }

  function tick() {
    var account = document.querySelector('shopify-account');
    if (!account || !account.shadowRoot) return;
    observe(account.shadowRoot);

    var loginForm = account.shadowRoot.querySelector('shopify-login-form');
    if (!loginForm || !loginForm.shadowRoot) return;
    observe(loginForm.shadowRoot);

    inject(loginForm.shadowRoot);
  }

  function start() {
    tick();
    // Catch the host element mounting/SPA navigation.
    new MutationObserver(tick).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    // Safety net for the first few seconds while the component boots.
    var attempts = 0;
    var poll = setInterval(function () {
      tick();
      if (++attempts > 40) clearInterval(poll);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
